import { MutableRefObject, useRef, useState } from 'react';

import { useSmartCaptchaLoader } from '@yandex/smart-captcha';

import { useCommonData } from '@stores/global-data';

import { YANDEX_SMART_CAPTCHA_TEST_MODE } from '@utils/env';
import { isValueSet } from '@utils/guards';
import { SnackBar } from '@utils/snackbar';

export type UseInvisibleCaptcha = () => {
    captchaRootRef: MutableRefObject<HTMLDivElement | null>;
    getCaptchaToken(): Promise<string>;
    captchaAPI: ReturnType<typeof useSmartCaptchaLoader>;
    isCaptchaLoading: boolean;
};

export const useInvisibleCaptcha: UseInvisibleCaptcha = () => {
    const captchaRootRef = useRef<HTMLDivElement>(null);

    const [isCaptchaLoading, setCaptchaLoading] = useState(false);

    const captchaAPI = useSmartCaptchaLoader();
    const { settings } = useCommonData();
    const { yandex_smart_captcha_key } = settings;

    const getCaptchaToken = async (): Promise<string> =>
        new Promise((resolve, reject) => {
            const handleReject = () => {
                SnackBar.handleApiError('Не удалось пройти автоматическую проверку');
                setCaptchaLoading(false);
                reject(new Error());
            };

            setCaptchaLoading(true);

            if (YANDEX_SMART_CAPTCHA_TEST_MODE) {
                setCaptchaLoading(false);
                resolve('test-captcha-token');
                return;
            }

            if (
                !isValueSet(captchaAPI) ||
                !isValueSet(captchaRootRef.current) ||
                !isValueSet(yandex_smart_captcha_key)
            ) {
                handleReject();
                return;
            }

            const { execute, subscribe, destroy, render } = captchaAPI;

            const id = render(captchaRootRef.current, {
                sitekey: yandex_smart_captcha_key,
                hl: 'ru',
                invisible: true,
                callback: resolve,
                test: YANDEX_SMART_CAPTCHA_TEST_MODE,
            });
            subscribe(id, 'network-error', () => {
                destroy(id);
                handleReject();
            });
            subscribe(id, 'challenge-hidden', () => {
                destroy(id);
                handleReject();
            });
            subscribe(id, 'success', () => {
                setCaptchaLoading(false);
                destroy(id);
            });
            execute(id);
        });

    return {
        captchaRootRef,
        captchaAPI,
        getCaptchaToken,
        isCaptchaLoading,
    };
};
